import { ReactElement, useRef } from 'react';
import type { FC, MouseEvent } from 'react';
import { Wrapper, List, Item, Prefix } from './styled';
import { CheckSolid } from '@simplywallst/ui-core/icons';
import { useEffect } from 'react';
import type { ExtraToggleProps } from '@components/Dropdown/components/Dropdown/Dropdown';

export interface Option {
  label: string;
  option: string;
  href?: string;
  selected?: boolean;
  level?: string;
}

export interface Props {
  visible: boolean;
  options: Option[];
  align: 'left' | 'right';
  scrollToSelected: boolean;
  selected: string;
  hoveredIndex: number;
  onBlur: () => void;
  onFocus: () => void;
  onSelect: (option: string, index: number) => void;
  searchable?: boolean;
  width?: number;
  extraItem?: (props: ExtraToggleProps) => ReactElement<ExtraToggleProps>;
  placeholder?: string;
  canSearch?: boolean;
}

const Options: FC<Props> = ({
  visible,
  options,
  align,
  hoveredIndex,
  onBlur,
  onFocus,
  onSelect,
  selected,
  scrollToSelected,
  searchable,
  width,
  extraItem,
  placeholder,
  canSearch,
}) => {
  const listRef = useRef<HTMLUListElement>(null);
  useEffect(() => {
    const { current } = listRef;
    if (current) {
      if (hoveredIndex > -1 && scrollToSelected) {
        const hoveredOption: HTMLLIElement | null = current.querySelector(
          '[data-is-hovering="true"]'
        );
        if (hoveredOption) {
          current.scrollTop = hoveredOption.offsetTop;
        }
      } else {
        current.scrollTop = 0;
      }
    }
  }, [hoveredIndex, scrollToSelected]);

  return (
    <Wrapper visible={visible}>
      <List
        ref={listRef}
        align={align}
        data-cy-id="dropdown-list"
        searchable={searchable}
        width={width}
      >
        {extraItem && extraItem({ onFocus, onBlur })}
        {placeholder && !canSearch && (
          <li>
            <Item
              as="a"
              level=""
              tabIndex={-1}
              onBlur={onBlur}
              onFocus={onFocus}
              disabled={true}
            >
              {placeholder}
            </Item>
          </li>
        )}
        {options.map((n, i) => (
          <li key={`dropdown-item-${n.option}-${n.label}-${i}`}>
            <Item
              as="a"
              level={n.level}
              href={n.href}
              data-cy-id="dropdown-list-item"
              tabIndex={-1}
              hovered={hoveredIndex === i}
              data-is-hovering={hoveredIndex === i}
              data-is-selected={selected === n.option}
              onBlur={onBlur}
              onFocus={onFocus}
              onClick={(e: MouseEvent) => {
                e.stopPropagation();
                e.preventDefault();
                selected !== n.option && onSelect(n.option, i);
              }}
              selected={selected === n.option}
              disabled={n.option.length === 0}
            >
              {n.option.length > 0 && selected === n.option && (
                <Prefix hovered={hoveredIndex === i}>
                  <CheckSolid />
                </Prefix>
              )}
              {n.label}
            </Item>
          </li>
        ))}
      </List>
    </Wrapper>
  );
};

export default Options;
